import type { CheatCommand, CheatGroup, CheatPanelSpec, CheatParamValues } from "./types.js";

export const RECENT_CHEAT_GROUP_ID = "__recent__";
export const RECENT_CHEAT_GROUP_LABEL = "최근 실행";
export const RECENT_CHEAT_GROUP_ORDER = -1;
export const RECENT_CHEAT_TTL_MS = 24 * 60 * 60 * 1000;
export const RECENT_CHEAT_MAX_ENTRIES = 8;

export interface CheatGroupModel {
  readonly group: CheatGroup;
  readonly commands: readonly CheatCommand[];
}

export interface RecentCheatEntry {
  readonly commandId: string;
  readonly executedAt: number;
}

export interface RecentCheatHistory {
  readonly savedAt: number;
  readonly entries: readonly RecentCheatEntry[];
}

export type CheatDigitShortcutInput = Pick<
  KeyboardEvent,
  "altKey" | "code" | "ctrlKey" | "isComposing" | "key" | "metaKey"
>;

export function commandHasParams(command: CheatCommand): boolean {
  return (command.params?.length ?? 0) > 0;
}

export function resolveCheatSuccessMessage(
  command: CheatCommand,
  values: CheatParamValues,
): string | false | undefined {
  const message = command.successMessage;
  if (typeof message === "function") return message(values);
  return message;
}

export function initialExpandedState(spec: CheatPanelSpec): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const group of spec.groups) state[group.id] = group.defaultExpanded;
  return state;
}

export function groupCheatCommands(spec: CheatPanelSpec): readonly CheatGroupModel[] {
  return spec.groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => (a.group.order ?? 0) - (b.group.order ?? 0) || a.index - b.index)
    .map(({ group }) => ({
      group,
      commands: spec.commands.filter((command) => command.group === group.id),
    }));
}

export function emptyRecentCheatHistory(nowMs: number): RecentCheatHistory {
  return { savedAt: nowMs, entries: [] };
}

export function parseRecentCheatHistory(raw: string | null, nowMs: number): RecentCheatHistory {
  if (!raw) return emptyRecentCheatHistory(nowMs);
  try {
    const parsed = JSON.parse(raw) as Partial<RecentCheatHistory>;
    const savedAt = typeof parsed.savedAt === "number" && Number.isFinite(parsed.savedAt)
      ? parsed.savedAt
      : nowMs;
    if (nowMs - savedAt >= RECENT_CHEAT_TTL_MS) return emptyRecentCheatHistory(nowMs);
    const entries = Array.isArray(parsed.entries)
      ? parsed.entries.flatMap((entry): RecentCheatEntry[] => {
          const value = entry as Partial<RecentCheatEntry>;
          if (typeof value.commandId !== "string" || value.commandId.length === 0) return [];
          const executedAt = typeof value.executedAt === "number" && Number.isFinite(value.executedAt)
            ? value.executedAt
            : savedAt;
          return [{ commandId: value.commandId, executedAt }];
        })
      : [];
    return { savedAt, entries: entries.slice(0, RECENT_CHEAT_MAX_ENTRIES) };
  } catch {
    return emptyRecentCheatHistory(nowMs);
  }
}

export function pushRecentCheatCommand(
  history: RecentCheatHistory,
  commandId: string,
  nowMs: number,
): RecentCheatHistory {
  const entries = [
    { commandId, executedAt: nowMs },
    ...history.entries.filter((entry) => entry.commandId !== commandId),
  ].slice(0, RECENT_CHEAT_MAX_ENTRIES);
  return { savedAt: nowMs, entries };
}

export function withRecentCheatGroup(
  spec: CheatPanelSpec,
  history: RecentCheatHistory,
): CheatPanelSpec {
  const recentCommands = history.entries.flatMap((entry): CheatCommand[] => {
    const command = spec.commands.find((candidate) => candidate.id === entry.commandId);
    return command ? [{ ...command, group: RECENT_CHEAT_GROUP_ID }] : [];
  });
  if (recentCommands.length === 0) return spec;
  const recentGroup: CheatGroup = {
    id: RECENT_CHEAT_GROUP_ID,
    label: RECENT_CHEAT_GROUP_LABEL,
    defaultExpanded: true,
    order: RECENT_CHEAT_GROUP_ORDER,
  };
  return {
    ...spec,
    groups: [recentGroup, ...spec.groups.filter((group) => group.id !== RECENT_CHEAT_GROUP_ID)],
    commands: [
      ...recentCommands,
      ...spec.commands.filter((command) => command.group !== RECENT_CHEAT_GROUP_ID),
    ],
  };
}

export function resolveRecentShortcutCommand(
  spec: CheatPanelSpec,
  shortcutIndex: number,
): CheatCommand | undefined {
  if (!Number.isInteger(shortcutIndex) || shortcutIndex < 0) return undefined;
  return spec.commands.filter((command) => command.group === RECENT_CHEAT_GROUP_ID)[shortcutIndex];
}

export function resolveControlDigitShortcut(event: CheatDigitShortcutInput): number | null {
  if (!event.ctrlKey || event.altKey || event.metaKey || event.isComposing) return null;
  if (/^[1-9]$/.test(event.key)) return Number(event.key) - 1;
  const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code)?.[1];
  return digit ? Number(digit) - 1 : null;
}
